import React from "react";
import "./Button.scss";
import Loader from "../Loader/Loader";
import mixpanelService from "../../Services/mixpanelService";
import mixpanelEvents, { getScreenName } from "../../../config/mixpanelEvents";
import { focusFirstInvalid } from "../../utils/a11y";

interface ButtonFields {
  btnName: string;
  disabled?: boolean;
  isLoading?: boolean | undefined;
  isSecondary?: boolean;
  isLightBlue?: boolean;
  type?: string;
  isTick?: boolean;
  isWhatsapp?: boolean;
  isUpload?: boolean;
  isAdd?: boolean;
  prefix?: any;
  clicked?: boolean;
  onClick?: (value: any) => void;
  parentClassName?: string;
  ariaLabel?: string;
  disabledReason?: string; // shown under a disabled Button/SubmitButton so users know what is missing
  focusInvalidOnClick?: boolean; // after click, focus the first aria-invalid field in the enclosing form
}

function trackCtaClick(props: any, event: any) {
  // a loading button stays focusable but must not fire again (double submit)
  if (props.isLoading) {
    event?.preventDefault?.();
    return;
  }
  try {
    const screen_name = getScreenName(window.location.pathname);
    if (screen_name) {
      mixpanelService({
        event: mixpanelEvents.CTA_CLICKED,
        screen_name,
        text: props.btnName,
        cta_type: "sticky",
        cta_class: props.isSecondary || props.isLightBlue ? "secondary" : "primary",
        cta_service: "AOJ",
      });
    }
  } catch (e) {
    console.error("mixpanel cta_clicked failed", e);
  }
  props.onClick?.(event);
  if (props.focusInvalidOnClick) {
    const btn = event?.currentTarget as HTMLElement | undefined;
    focusFirstInvalid(btn?.closest("form") || btn?.closest("main") || null, btn);
  }
}

// Visible "why is this disabled" line, linked to the button via aria-describedby
const DisabledReason: React.FC<{ id: string; text?: string }> = ({ id, text }) =>
  text ? (
    <p className="btn_disabled_reason" id={id}>
      {text}
    </p>
  ) : null;

const Button: React.FC<ButtonFields> = (props: any) => {
  const reasonId = React.useId();
  const showReason = props.disabled && !props.isLoading && props.disabledReason;
  return (
    <>
      <button
        className={
          "btn " +
          (props.isSecondary ? "isSecondary " : "") +
          (props.isLightBlue ? "isLightBlue " : "") +
          (props.disabled || props.isLoading ? "disable" : "active") +
          (props?.parentClassName ? props?.parentClassName : "")
        }
        disabled={props.disabled}
        onClick={(event) => trackCtaClick(props, event)}
        type={props.type || "button"}
        aria-busy={props.isLoading || undefined}
        aria-disabled={props.isLoading || undefined}
        aria-label={props.ariaLabel || props.btnName || undefined}
        aria-describedby={showReason ? reasonId : undefined}
      >
        {props.prefix && props.prefix}
        {props.isLoading ? (
          <>
            <Loader announce size={20} />
            <span className="sr-only">{props.btnName || "Loading"}</span>
          </>
        ) : props.isTick ? (
          <>
            {" "}
            <span className="edel-icon-tick" aria-hidden="true" /> {props.btnName}
          </>
        ) : (
          `${props.btnName}`
        )}
      </button>
      {showReason && <DisabledReason id={reasonId} text={props.disabledReason} />}
    </>
  );
};

export const DashedButton: React.FC<ButtonFields> = (props: any) => (
  <button
    className={"addBtn " + (props.disabled ? "dasDisable" : "dasActive")}
    disabled={props.disabled}
    onClick={(event) => trackCtaClick(props, event)}
    type={props.type || "button"}
    aria-busy={props.isLoading || undefined}
    aria-label={props.ariaLabel || props.btnName || undefined}
  >
    {/* {props.btnName}
        {props.isLoading && <Loader announce size={20} />} */}
    {props.isLoading ? (
      <>
        <Loader announce size={20} />
        <span className="sr-only">{props.btnName || "Loading"}</span>
      </>
    ) : props.isAdd ? (
      <>
        {" "}
        <span className="edel-icon-plus" aria-hidden="true" /> {props.btnName}
      </>
    ) : (
      `${props.btnName}`
    )}
  </button>
);

export const TransparentButton: React.FC<ButtonFields> = (props: any) => (
  <button
    className={"trnsBtn " + props.disabled}
    disabled={props.disabled}
    onClick={(event) => trackCtaClick(props, event)}
    type={props.type || "button"}
    aria-busy={props.isLoading || undefined}
    aria-label={props.ariaLabel || props.btnName || (props.isUpload ? "Upload" : props.isWhatsapp ? "WhatsApp" : undefined)}
  >
    {props.prefix && props.prefix}
    {props.isWhatsapp ? (
      <>
        {" "}
        <span className="edel-icon-whatsapp" aria-hidden="true" />{" "}
      </>
    ) : null}
    {props.isUpload ? (
      <>
        {" "}
        <span className="edel-icon-upload" aria-hidden="true" />{" "}
      </>
    ) : null}
    {props.isLoading ? (
      <>
        <Loader announce size={20} />
        <span className="sr-only">{props.btnName || "Loading"}</span>
      </>
    ) : props.isTick ? (
      <>
        {" "}
        <span className="edel-icon-tick" aria-hidden="true" /> {props.btnName}
      </>
    ) : (
      `${props.btnName}`
    )}
  </button>
);

export const SubmitButton: React.FC<ButtonFields> = (props: any) => {
  const reasonId = React.useId();
  const showReason = props.disabled && !props.isLoading && props.disabledReason;
  return (
    <>
      <button
        className={"submitBtn " + (props.disabled ? "disable" : "active")}
        disabled={props.disabled}
        onClick={(event) => trackCtaClick(props, event)}
        type={props.type || "submit"}
        aria-busy={props.isLoading || undefined}
        aria-disabled={props.isLoading || undefined}
        aria-label={props.ariaLabel || props.btnName || undefined}
        aria-describedby={showReason ? reasonId : undefined}
      >
        {props.isLoading ? (
          <>
            <Loader announce size={20} />
            <span className="sr-only">{props.btnName || "Loading"}</span>
          </>
        ) : props.isTick ? (
          <>
            {" "}
            <span className="edel-icon-tick" aria-hidden="true" /> {props.btnName}
          </>
        ) : (
          `${props.btnName}`
        )}
      </button>
      {showReason && <DisabledReason id={reasonId} text={props.disabledReason} />}
    </>
  );
};

export const Verify: React.FC<ButtonFields> = (props: any) => (
  <button
    className={
      "btn vrfyBtn " +
      (props.disabled || props.isLoading ? "disable" : "active")
    }
    disabled={props.disabled}
    onClick={(event) => trackCtaClick(props, event)}
    type={props.type || "button"}
    aria-busy={props.isLoading || undefined}
    aria-label={props.ariaLabel || props.btnName || undefined}
  >
    {props.isLoading ? (
      <>
        <Loader announce size={20} />
        <span className="sr-only">{props.btnName || "Loading"}</span>
      </>
    ) : props.isTick ? (
      <>
        {" "}
        <span className="edel-icon-tick" aria-hidden="true" />
        {props.btnName}
      </>
    ) : (
      `${props.btnName}`
    )}
  </button>
);

export const LightBlueButton: React.FC<ButtonFields> = (props: any) => (
  <button
    className={
      "LightBluebtn info alignBtn " + (props.clicked ? "selected" : "")
    }
    disabled={props.disabled}
    onClick={(event) => trackCtaClick(props, event)}
    type={props.type || "button"}
    aria-busy={props.isLoading || undefined}
    aria-label={props.ariaLabel || props.btnName || (props.isUpload ? "Upload" : props.isWhatsapp ? "WhatsApp" : undefined)}
  >
    {props.prefix && props.prefix}
    {props.isWhatsapp ? (
      <>
        {" "}
        <span className="edel-icon-whatsapp" aria-hidden="true" />{" "}
      </>
    ) : null}
    {props.isUpload ? (
      <>
        {" "}
        <span className="edel-icon-upload" aria-hidden="true" />{" "}
      </>
    ) : null}
    {props.isLoading ? (
      <>
        <Loader announce size={20} />
        <span className="sr-only">{props.btnName || "Loading"}</span>
      </>
    ) : props.isTick ? (
      <>
        {" "}
        <span className="edel-icon-tick" aria-hidden="true" /> {props.btnName}
      </>
    ) : (
      `${props.btnName}`
    )}
  </button>
);

export default Button;
